import React from "react";
import { Link } from "gatsby";
import "../assets/fonts/inter.css";
import styled, { createGlobalStyle } from "styled-components";
import Layout from "../components/Layout";
import Adsense from "react-adsense";
import ReactGA from "react-ga";
import FeaturedCarousel from "../components/FeaturedCarousel";
import CompactCard from "../components/CompactCard";
ReactGA.initialize("UA-141669883-1");

export const GlobalStyle = createGlobalStyle`
  /* ── Light mode variables (default) ── */
  :root {
    --bg:                #ffffff;
    --bg-secondary:      #f9fafb;
    --bg-tertiary:       #f3f4f6;
    --nav-bg:            #ffffff;
    --nav-border:        #e5e7eb;
    --text-primary:      #111827;
    --text-secondary:    #374151;
    --text-muted:        #6b7280;
    --border:            #e5e7eb;
    --card-bg:           #ffffff;
    --section-header-bg: #f3f4f6;
    --section-header-text:#374151;
    --link:              #2563eb;
    --link-hover:        #1d4ed8;
    --dot-bg:            #ef4444;
    --hover-bg:          #f9fafb;
    --compact-title:     #1f2937;
    --compact-title-hover:#2563eb;
  }

  /* ── Dark mode variables ── */
  html.dark {
    --bg:                #0f172a;
    --bg-secondary:      #1e293b;
    --bg-tertiary:       #1e293b;
    --nav-bg:            #0f172a;
    --nav-border:        #334155;
    --text-primary:      #f1f5f9;
    --text-secondary:    #cbd5e1;
    --text-muted:        #94a3b8;
    --border:            #334155;
    --card-bg:           #1e293b;
    --section-header-bg: #1e293b;
    --section-header-text:#cbd5e1;
    --link:              #60a5fa;
    --link-hover:        #93c5fd;
    --dot-bg:            #f87171;
    --hover-bg:          #1e293b;
    --compact-title:     #f1f5f9;
    --compact-title-hover:#60a5fa;
  }

  html {
    font-family: 'Inter', Arial, Helvetica, sans-serif;
    font-size: 16px;
  }
  body {
    margin: 0;
    box-sizing: border-box;
    background: var(--bg);
    color: var(--text-primary);
    transition: background 0.2s, color 0.2s;
  }
  * { box-sizing: border-box; }
  p { margin: 0; }
  ins.adsbygoogle[data-ad-status='unfilled'] { display: none !important; }
  .ad-container:has(ins.adsbygoogle[data-ad-status='unfilled']) { display: none !important; }
  .ad-container:empty { display: none !important; }
`;

/* ─── Layout containers ─── */

const PageSection = styled.section`
  padding: 1rem 0;
  background: var(--bg);
  @media (min-width: 768px) { padding: 1.5rem 0; }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  @media (min-width: 640px) { padding: 0 1.5rem; }
  @media (min-width: 1024px) { padding: 0 2rem; }
`;

/* main col (8) + sidebar (4) */
const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  @media (min-width: 1024px) {
    grid-template-columns: 2fr 1fr;
    gap: 1rem;
  }
`;

/* side-by-side sections */
const SplitGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  @media (min-width: 600px) {
    grid-template-columns: 1fr 1fr;
    gap: 0;
    border: 1px solid var(--border);
  }
`;

const SplitLeft = styled.div`
  border: 1px solid var(--border);
  @media (min-width: 600px) {
    border: none;
    border-right: 1px solid var(--border);
  }
`;

const SplitRight = styled.div`
  border: 1px solid var(--border);
  @media (min-width: 600px) { border: none; }
`;

const MainColStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SidebarStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

/* ─── Section box ─── */

const SectionBox = styled.div`
  border: 1px solid var(--border);
`;

const SectionHeader = styled.div`
  background: var(--section-header-bg);
  border-bottom: 1px solid var(--border);
  padding: 0.5rem 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--section-header-text);
  text-transform: uppercase;
  margin: 0;
  letter-spacing: 0.05em;
`;

const ReadMore = styled(Link)`
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--link);
  text-decoration: none;
  &:hover { color: var(--link-hover); }
`;

const SectionBody = styled.div`
  background: var(--card-bg);
`;

/* ─── Latest updates list ─── */

const LatestTitle = styled.span`
  font-size: 0.875rem;
  font-weight: ${props => (props.bold ? 700 : 500)};
  color: var(--compact-title);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
  transition: color 0.15s;
`;

const LatestItem = styled(Link)`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid var(--border);
  text-decoration: none;
  background: var(--card-bg);
  transition: background 0.15s;
  &:last-child { border-bottom: none; }
  &:hover { background: var(--hover-bg); }
  &:hover ${LatestTitle} { color: var(--compact-title-hover); }
`;

const RedDot = styled.span`
  width: 0.5rem;
  height: 0.5rem;
  background: var(--dot-bg);
  border-radius: 9999px;
  flex-shrink: 0;
  margin-top: 0.35rem;
`;

/* ─── Ad container ─── */

const AdWrap = styled.div`
  width: 100%;
`;

const EmptyMsg = styled.div`
  padding: 1rem 0.75rem;
  font-size: 0.75rem;
  color: #9ca3af;
  text-align: center;
`;

/* Mobile-only / desktop-only wrappers for swapping Latest Updates position */
const MobileOnly = styled.div`
  display: block;
  @media (min-width: 1024px) { display: none; }
`;

const DesktopOnly = styled.div`
  display: none;
  @media (min-width: 1024px) { display: block; }
`;

/* ─── Component ─── */

class HomePage extends React.Component {
  constructor(props) {
    super(props);
    this.topOneCards = [];
    this.topTwoCards = [];
    this.topThreeCards = [];
    this.topFourCards = [];
    this.reviewCards = [];
    this.galleryCards = [];
    this.allCards = [];
  }

  componentWillMount() {
    const { data } = this.props;
    if (!data) return;

    const checkAndPick = (docObj) => {
      const tags = docObj.node.tags || [];
      if (tags.includes("one") && this.topOneCards.length === 0) this.topOneCards.push(docObj);
      else if (tags.includes("two") && this.topTwoCards.length === 0) this.topTwoCards.push(docObj);
      else if (tags.includes("three") && this.topThreeCards.length === 0) this.topThreeCards.push(docObj);
      else if (tags.includes("four") && this.topFourCards.length === 0) this.topFourCards.push(docObj);
    };

    if (data.review) {
      data.review.edges.slice(0, 20).forEach(e => {
        this.reviewCards.push(e);
        checkAndPick(e);
      });
    }
    if (data.all) {
      data.all.edges.slice(0, 50).forEach(e => {
        this.allCards.push(e);
        checkAndPick(e);
      });
    }
    if (data.gallery) {
      data.gallery.edges.slice(0, 20).forEach(e => {
        this.galleryCards.push(e);
        checkAndPick(e);
      });
    }

    // Also pick from dedicated one/two/three/four queries
    if (data.one && data.one.edges.length > 0 && this.topOneCards.length === 0) this.topOneCards = data.one.edges;
    if (data.two && data.two.edges.length > 0 && this.topTwoCards.length === 0) this.topTwoCards = data.two.edges;
    if (data.three && data.three.edges.length > 0 && this.topThreeCards.length === 0) this.topThreeCards = data.three.edges;
    if (data.four && data.four.edges.length > 0 && this.topFourCards.length === 0) this.topFourCards = data.four.edges;
  }

  render() {
    ReactGA.pageview("AndhraHeadlines HomePage");

    const { data } = this.props;
    if (!data) return null;

    // Carousel articles
    const featuredArticles = [
      this.topOneCards[0],
      this.topTwoCards[0],
      this.topThreeCards[0],
      this.topFourCards[0],
    ].filter(Boolean);

    // Latest updates — show 15; first 5 emphasized in bold, remaining 10 in normal weight
    const latestUpdates = this.allCards.slice(0, 15);
    const latestUpdatesPanel = (
      <SectionBox>
        <SectionHeader>
          <SectionTitle>Latest Updates</SectionTitle>
        </SectionHeader>
        <SectionBody>
          {latestUpdates.length > 0 ? (
            latestUpdates.map((card, i) => (
              <LatestItem key={i} to={`/${card.node.uid}/`}>
                <RedDot />
                <LatestTitle bold={i < 5}>{card.node.data.title.text}</LatestTitle>
              </LatestItem>
            ))
          ) : (
            <EmptyMsg>No updates available</EmptyMsg>
          )}
        </SectionBody>
      </SectionBox>
    );

    // Section articles from tag queries
    const politicsArticles = data.politics ? data.politics.edges : [];
    const entertainmentArticles = data.entertainment ? data.entertainment.edges : [];
    const healthArticles = data.health ? data.health.edges : [];
    const nriArticles = data.nri ? data.nri.edges : [];
    const videosArticles = data.videos ? data.videos.edges : [];
    const editorialArticles = data.editorial ? data.editorial.edges : [];

    // Reviews (first 4) and gallery (first 4)
    const reviewArticles = this.reviewCards.slice(0, 4);
    const galleryArticles = this.galleryCards.slice(0, 4);


    return (
      <>
        <GlobalStyle />
        <Layout>
          {/* ── Unified Grid: Carousel + main content (left), Latest Updates + sidebar (right) ── */}
          <PageSection>
            <Container>
              <MainGrid>
                {/* ── Left column ── */}
                <MainColStack>
                  {/* Featured Carousel — fixed size, leads the left column */}
                  <FeaturedCarousel articles={featuredArticles} />

                  {/* Mobile-only: Latest Updates appears right after the carousel on small screens */}
                  <MobileOnly>{latestUpdatesPanel}</MobileOnly>

                  {/* Ad */}
                  <AdWrap className="ad-container">
                    <Adsense.Google
                      client="ca-pub-5630104718741028"
                      slot="9699731559"
                      style={{ display: 'block', width: '100%', minHeight: 90 }}
                      format="auto"
                      responsive="true"
                    />
                  </AdWrap>

                  {/* Politics + Entertainment */}
                  <SplitGrid>
                    <SplitLeft>
                      <SectionHeader>
                        <SectionTitle>Politics</SectionTitle>
                        {politicsArticles.length > 0 && (
                          <ReadMore to="/category/politics">Read More →</ReadMore>
                        )}
                      </SectionHeader>
                      <SectionBody>
                        {politicsArticles.length > 0 ? (
                          politicsArticles.map(({ node }) => (
                            <CompactCard
                              key={node.uid}
                              title={node.data.title.text}
                              img={node.data.img ? node.data.img.url : ''}
                              uid={node.uid}
                            />
                          ))
                        ) : (
                          <EmptyMsg>No politics articles available</EmptyMsg>
                        )}
                      </SectionBody>
                    </SplitLeft>

                    <SplitRight>
                      <SectionHeader>
                        <SectionTitle>Entertainment</SectionTitle>
                        {entertainmentArticles.length > 0 && (
                          <ReadMore to="/category/entertainment">Read More →</ReadMore>
                        )}
                      </SectionHeader>
                      <SectionBody>
                        {entertainmentArticles.length > 0 ? (
                          entertainmentArticles.map(({ node }) => (
                            <CompactCard
                              key={node.uid}
                              title={node.data.title.text}
                              img={node.data.img ? node.data.img.url : ''}
                              uid={node.uid}
                            />
                          ))
                        ) : (
                          <EmptyMsg>No entertainment articles available</EmptyMsg>
                        )}
                      </SectionBody>
                    </SplitRight>
                  </SplitGrid>

                  {/* Ad between Politics/Entertainment and Reviews/Gallery */}
                  <AdWrap className="ad-container">
                    <Adsense.Google
                      client="ca-pub-5630104718741028"
                      slot="9699731559"
                      style={{ display: 'block', width: '100%', minHeight: 90 }}
                      format="auto"
                      responsive="true"
                    />
                  </AdWrap>

                  {/* Movie Reviews + Gallery */}
                  <SplitGrid>
                    <SplitLeft>
                      <SectionHeader>
                        <SectionTitle>Movie Reviews</SectionTitle>
                        {this.reviewCards.length > 4 && (
                          <ReadMore to="/reviews">Read More →</ReadMore>
                        )}
                      </SectionHeader>
                      <SectionBody>
                        {reviewArticles.length > 0 ? (
                          reviewArticles.map(({ node }) => (
                            <CompactCard
                              key={node.uid}
                              title={node.data.title.text}
                              img={node.data.img ? node.data.img.url : ''}
                              uid={node.uid}
                            />
                          ))
                        ) : (
                          <EmptyMsg>No movie reviews available</EmptyMsg>
                        )}
                      </SectionBody>
                    </SplitLeft>

                    <SplitRight>
                      <SectionHeader>
                        <SectionTitle>Gallery</SectionTitle>
                        {this.galleryCards.length > 4 && (
                          <ReadMore to="/gallery">Read More →</ReadMore>
                        )}
                      </SectionHeader>
                      <SectionBody>
                        {galleryArticles.length > 0 ? (
                          galleryArticles.map(({ node }) => (
                            <CompactCard
                              key={node.uid}
                              title={node.data.title.text}
                              img={node.data.img ? node.data.img.url : ''}
                              uid={`/${node.uid}`}
                            />
                          ))
                        ) : (
                          <EmptyMsg>No gallery articles available</EmptyMsg>
                        )}
                      </SectionBody>
                    </SplitRight>
                  </SplitGrid>

                  {/* Ad between Reviews/Gallery and Videos */}
                  <AdWrap className="ad-container">
                    <Adsense.Google
                      client="ca-pub-5630104718741028"
                      slot="9699731559"
                      style={{ display: 'block', width: '100%', minHeight: 90 }}
                      format="auto"
                      responsive="true"
                    />
                  </AdWrap>

                  {/* Videos */}
                  <SectionBox>
                    <SectionHeader>
                      <SectionTitle>Videos</SectionTitle>
                      {videosArticles.length > 0 && (
                        <ReadMore to="/category/videos">Read More →</ReadMore>
                      )}
                    </SectionHeader>
                    <SectionBody>
                      {videosArticles.length > 0 ? (
                        videosArticles.map(({ node }) => (
                          <CompactCard
                            key={node.uid}
                            title={node.data.title.text}
                            img={node.data.img ? node.data.img.url : ''}
                            uid={node.uid}
                          />
                        ))
                      ) : (
                        <EmptyMsg>No videos available</EmptyMsg>
                      )}
                    </SectionBody>
                  </SectionBox>
                </MainColStack>

                {/* ── Sidebar ── */}
                <SidebarStack>
                  {/* Desktop-only: Latest Updates at top of sidebar, flows down past the carousel */}
                  <DesktopOnly>{latestUpdatesPanel}</DesktopOnly>

                  {/* NRI Updates */}
                  <SectionBox>
                    <SectionHeader>
                      <SectionTitle>NRI / USA Updates</SectionTitle>
                      {nriArticles.length > 0 && (
                        <ReadMore to="/category/nri">Read More →</ReadMore>
                      )}
                    </SectionHeader>
                    <SectionBody>
                      {nriArticles.length > 0 ? (
                        nriArticles.map(({ node }) => (
                          <CompactCard
                            key={node.uid}
                            title={node.data.title.text}
                            img={node.data.img ? node.data.img.url : ''}
                            uid={node.uid}
                          />
                        ))
                      ) : (
                        <EmptyMsg>No NRI/USA updates available</EmptyMsg>
                      )}
                    </SectionBody>
                  </SectionBox>

                  {/* Sidebar Ad */}
                  <AdWrap className="ad-container">
                    <Adsense.Google
                      client="ca-pub-5630104718741028"
                      slot="9368762673"
                      style={{ display: 'block', width: '100%', minHeight: 280 }}
                      format="auto"
                      responsive="true"
                    />
                  </AdWrap>

                  {/* Editorial */}
                  <SectionBox>
                    <SectionHeader>
                      <SectionTitle>Editorial Column</SectionTitle>
                      {editorialArticles.length > 0 && (
                        <ReadMore to="/category/editorial">Read More →</ReadMore>
                      )}
                    </SectionHeader>
                    <SectionBody>
                      {editorialArticles.length > 0 ? (
                        editorialArticles.map(({ node }) => (
                          <CompactCard
                            key={node.uid}
                            title={node.data.title.text}
                            img={node.data.img ? node.data.img.url : ''}
                            uid={node.uid}
                          />
                        ))
                      ) : (
                        <EmptyMsg>No editorial articles available</EmptyMsg>
                      )}
                    </SectionBody>
                  </SectionBox>

                  {/* Health */}
                  <SectionBox>
                    <SectionHeader>
                      <SectionTitle>Health</SectionTitle>
                      {healthArticles.length > 0 && (
                        <ReadMore to="/category/health">Read More →</ReadMore>
                      )}
                    </SectionHeader>
                    <SectionBody>
                      {healthArticles.length > 0 ? (
                        healthArticles.map(({ node }) => (
                          <CompactCard
                            key={node.uid}
                            title={node.data.title.text}
                            img={node.data.img ? node.data.img.url : ''}
                            uid={node.uid}
                          />
                        ))
                      ) : (
                        <EmptyMsg>No health articles available</EmptyMsg>
                      )}
                    </SectionBody>
                  </SectionBox>
                </SidebarStack>
              </MainGrid>
            </Container>
          </PageSection>

          {/* Bottom Ad */}
          <PageSection>
            <Container>
              <AdWrap className="ad-container">
                <Adsense.Google
                  client="ca-pub-5630104718741028"
                  slot="9699731559"
                  style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto', minHeight: 90 }}
                  format="auto"
                  responsive="true"
                />
              </AdWrap>
            </Container>
          </PageSection>
        </Layout>
      </>
    );
  }
}

export default HomePage;
